import React, { useEffect, useRef, useCallback, useContext, useState } from "react";
import { BsChevronLeft, BsChevronRight, BsTrash } from "react-icons/bs";
import { FaChevronLeft } from "react-icons/fa";
import { AuthContext } from "../../contexts/AuthContext";
import { DisplayDiscussionForMiniScreenContext } from "../../contexts/DisplayDiscussionForMiniScreenContext";
import { env } from "../../datas.config";
import { getBgGradient, getFontFamily } from "./CreateAnnonceModal";
import Person from "../Person";

export default function AnnonceViewer({ annonces, activeIndex, onSlideChange, onDelete }) {
  const { user } = useContext(AuthContext);
  const { displayDiscussionForMiniScreen, setDisplayDiscussionForMiniScreen } =
    useContext(DisplayDiscussionForMiniScreenContext);
  const timerRef = useRef(null);
  const progressKeyRef = useRef(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const startAutoAdvance = useCallback(() => {
    clearTimeout(timerRef.current);
    progressKeyRef.current += 1;
    timerRef.current = setTimeout(() => {
      if (activeIndex < annonces.length - 1) {
        onSlideChange(activeIndex + 1);
      }
    }, 5000);
  }, [activeIndex, annonces.length, onSlideChange]);

  useEffect(() => {
    if (annonces.length > 0) {
      startAutoAdvance();
    }
    return () => clearTimeout(timerRef.current);
  }, [activeIndex, startAutoAdvance, annonces.length]);

  useEffect(() => {
    setConfirmDelete(false);
  }, [activeIndex]);

  if (annonces.length === 0) {
    return (
      <div className={`tyn-main ${displayDiscussionForMiniScreen ? "main-shown" : ""}`}>
        <div className="stories-viewer">
          <div className="text-center text-muted">
            <p style={{ fontSize: "1.2rem" }}>Aucune annonce à afficher</p>
            <p>Créez une annonce avec le bouton "+" ci-dessus</p>
          </div>
        </div>
      </div>
    );
  }

  const annonce = annonces[activeIndex];
  if (!annonce) return null;

  const isEmetteur = annonce.emetteur?._id === user.userId;

  const goPrev = () => {
    if (activeIndex > 0) onSlideChange(activeIndex - 1);
  };

  const goNext = () => {
    if (activeIndex < annonces.length - 1) onSlideChange(activeIndex + 1);
  };

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    onDelete(annonce._id);
    setConfirmDelete(false);
  };

  return (
    <div className={`tyn-main ${displayDiscussionForMiniScreen ? "main-shown" : ""}`}>
      <div className="stories-viewer">
        {activeIndex > 0 && (
          <button className="stories-nav-btn stories-nav-prev" onClick={goPrev}>
            <BsChevronLeft />
          </button>
        )}

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 480, width: "100%" }}>
          {/* Header : bouton retour (mobile) + bouton supprimer */}
          <div className="d-flex align-items-center w-100 mb-2" style={{ minHeight: 40 }}>
            <button
              className="btn btn-icon btn-md btn-pill btn-transparent d-md-none"
              onClick={() => setDisplayDiscussionForMiniScreen(false)}
            >
              <FaChevronLeft />
            </button>
            <div className="ms-auto">
              {isEmetteur && (
                <button
                  onClick={handleDelete}
                  className="btn btn-sm"
                  style={{
                    background: confirmDelete ? "rgba(220,53,69,0.9)" : "rgba(0,0,0,0.08)",
                    color: confirmDelete ? "#fff" : "#666",
                    borderRadius: 20,
                    padding: "6px 14px",
                    border: "none",
                  }}
                >
                  <BsTrash className="me-1" />
                  {confirmDelete ? "Confirmer" : "Supprimer"}
                </button>
              )}
            </div>
          </div>

          {/* Card */}
          <div
            className="swiper annonce-swiper"
            style={{
              width: "100%",
              maxWidth: 480,
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <div className="stories-slide-inner">
              {/* Progress bar */}
              <div className="stories-progress-bar">
                {annonces.map((_, i) => (
                  <div
                    key={i}
                    className={`stories-progress-segment ${
                      i < activeIndex ? "viewed" : ""
                    } ${i === activeIndex ? "active" : ""}`}
                  >
                    {i === activeIndex && (
                      <div
                        key={progressKeyRef.current}
                        className="stories-progress-fill"
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Contenu */}
              {annonce.type === "image" ? (
                <img
                  src={`${env.domain}/api/auth/annonces/${annonce.contenu?.imageUrl}`}
                  alt="Annonce"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: getBgGradient(annonce.bgColor),
                    padding: "40px",
                  }}
                >
                  <p
                    style={{
                      color: "#fff",
                      fontFamily: getFontFamily(annonce.fontFamily),
                      fontSize:
                        typeof annonce.contenu === "string" &&
                        annonce.contenu.length > 150
                          ? "1.1rem"
                          : "1.4rem",
                      textAlign: "center",
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      margin: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {annonce.contenu}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Infos émetteur — en dehors de la publication */}
          <div className="w-100 mt-3 px-1">
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: 32, height: 32 }}>
                <Person
                  person={{
                    image: annonce.emetteur?.userImage,
                    nom: annonce.emetteur?.nom,
                    prenom: annonce.emetteur?.prenom,
                    userId: annonce.emetteur?._id,
                    colors: annonce.emetteur?.color,
                  }}
                />
              </div>
              <h6 style={{ margin: 0, fontSize: "0.95rem" }}>
                {annonce.emetteur?.prenom} {annonce.emetteur?.nom}
              </h6>
            </div>
            {annonce.type === "image" && annonce.contenu?.legende && (
              <p className="text-muted mt-1 mb-0" style={{ fontSize: "0.85rem" }}>
                {annonce.contenu.legende}
              </p>
            )}
          </div>
        </div>

        {activeIndex < annonces.length - 1 && (
          <button className="stories-nav-btn stories-nav-next" onClick={goNext}>
            <BsChevronRight />
          </button>
        )}
      </div>
    </div>
  );
}
